/************************************************************************************************
PkTransformLink ********************************************************************************
************************************************************************************************/
if (!customElements.get("pk-transform-link")) {
  /**
   * Example of use:
   *  <pk-transform-link 
   *    data-link="{{ link.url }}" 
   *    data-id="{{ section.id }}"
   *  >
   *    {{ link.title }}
   *  </pk-transform-link>
   */
  class PkTransformLink extends HTMLElement {
    connectedCallback() {
      const originalLink = this.getAttribute("data-link");
      const id = this.getAttribute("data-id");

      if (!originalLink) {
        console.warn("Missing data-link", { id });
        return;
      }

      // Check whether the link is already obfuscated
      let encodedLink = originalLink;
    
      try {
        decodeURIComponent(escape(atob(originalLink)));
        // console.log(`[pk-obfuscated-link] ${id}: already obfuscated, skipping re-encoding`);
      } catch {
        encodedLink = this.obfuscate(originalLink);
    
        if (!encodedLink) {
          console.error("Obfuscation failure", { id, originalLink });
          return;
        }
    
        this.setAttribute("data-link", encodedLink);
      }

      this.setAttribute("role", "link");
      this.setAttribute("tabindex", "0");
      this.setAttribute("aria-label", this.textContent.trim());
      this.style.cursor = "pointer";

      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeydown);
    }

    handleClick = () => {
      this.navigate();
    };

    handleKeydown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.navigate();
      }
    };

    /**
     * Decode the obfuscated data-link attribute and navigate the window to that URL.
     */
    navigate() {
      const encodedLink = this.getAttribute("data-link");
      const id = this.getAttribute("data-id");

      if (!encodedLink) {
        console.warn("No encoded data-link", { id });
        return;
      }

      const decodedLink = this.deobfuscate(encodedLink);

      if (decodedLink) {
        window.location.href = decodedLink;
      } else {
        console.error("Decoding failed", {
          id,
          encodedLink,
          text: this.textContent
        });
      }
    }

    /**
     * Obfuscate a string by Base64-encoding its UTF-8 representation.
     * @param {string} string - The original URL or text to obfuscate.
     * @returns {string|null} - Base64-encoded string, or null on error.
     */
    obfuscate(string) {
      try {
        return btoa(unescape(encodeURIComponent(string)));
      } catch (error) {
        console.error("String obfuscation error :", error);
        return null;
      }
    }

    /**
     * Deobfuscate a string by Base64-decoding and then decoding UTF-8.
     * @param {string} encodedString - The Base64-encoded string.
     * @returns {string|null} - The original string, or null on error.
     */
    deobfuscate(encodedString) {
      try {
        return decodeURIComponent(escape(atob(encodedString)));
      } catch (error) {
        console.error("String deobfuscation error :", error);
        return null;
      }
    }
  }

  customElements.define("pk-transform-link", PkTransformLink);
}

/************************************************************************************************
PkModal *****************************************************************************************
************************************************************************************************/
if (!customElements.get('pk-modal')) {
  class PKModal extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.init();
    }

    init() {
      this.render();
    }

    connectedCallback() {
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      this.modal = this.shadowRoot.querySelector(".modal");
      this.backdrop = this.shadowRoot.querySelector(".backdrop");
      this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);
      this.closeSlot = this.shadowRoot.querySelector("slot[name='close']");

      this.handleEscape = this.handleEscape.bind(this);
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleSlotChange = this.handleSlotChange.bind(this);

      if (this.trigger) {
        this.trigger.addEventListener("click", this.handleTriggerClick);
      } else {
        console.warn(
          `PKModal: No trigger found for modal with id "${this.id}". ` +
          `Ensure an element has [aria-controls="${this.id}"].`
        );
      }

      this.initEvents();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.handleEscape);
      
      this.trigger?.removeEventListener("click", this.handleTriggerClick);
      this.backdrop?.removeEventListener("click", this.handleBackdropClick);
      this.closeSlot?.removeEventListener("slotchange", this.handleSlotChange);
      
      if (this.closeBtn) {
        this.closeBtn.removeEventListener("click", this.handleCloseClick);
        this.closeBtn = null;
      }
    }

    toggleModal(open) {
      const isOpen = this.hasAttribute("open");
      if (open === isOpen) return;

      this.toggleAttribute("open", open);
      this.trigger?.setAttribute("aria-expanded", open ? "true" : "false");
      document.body.style.overflow = open ? "hidden" : "";

      if (open) {
        this.modal.setAttribute("tabindex", "-1");
        this.modal.focus();
        document.addEventListener("keydown", this.handleEscape);
        this.dispatchEvent(new CustomEvent("open", {
          detail: { id: this.id, content: this.innerHTML }
        })); 
      } else {
        this.trigger?.focus();
        document.removeEventListener("keydown", this.handleEscape);
        this.dispatchEvent(new CustomEvent("close", {
          detail: { id: this.id }
        }));
      }
    }

    initEvents() {
      this.backdrop?.addEventListener("click", this.handleBackdropClick);
      this.closeSlot?.addEventListener("slotchange", this.handleSlotChange);
    }

    handleSlotChange() {
      const newCloseBtn = this.querySelector('[slot="close"]');

      if (this.closeBtn && this.closeBtn !== newCloseBtn) {
        this.closeBtn.removeEventListener("click", this.handleCloseClick);
      }

      this.closeBtn = newCloseBtn;

      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", this.handleCloseClick);
      }
    }

    handleTriggerClick() {
      this.toggleModal(true);
    }

    handleBackdropClick() {
      this.toggleModal(false);
    }

    handleCloseClick() {
      this.toggleModal(false);
    }

    handleEscape(event) {
      if (event.key === "Escape") {
        this.toggleModal(false);
      }
    }

    render() {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: var(--pk-modal-overlay-color, rgba(0, 0, 0, 0.7));
            justify-content: center;
            align-items: center;
            z-index: 2147483647;
          }

          .modal {
            margin: var(--pk-modal-margin, 0);
            padding: var(--pk-modal-padding, 40px);
            position: relative;
            display: flex;
            flex-direction: column;
            width: var(--pk-modal-width, 100%);
            min-width: var(--pk-modal-min-width, none);
            max-width: var(--pk-modal-max-width, 80%);
            height: var(--pk-modal-height, 100%);
            min-height: var(--pk-modal-min-height, none);
            max-height: var(--pk-modal-max-height, 70%);
            color: var(--pk-modal-color, initial);
            background: var(--pk-modal-background, white);
            border-radius: var(--pk-modal-border-radius, 0);
            opacity: 0;
            transform: scale(0.9);
            transition: transform 0.2s ease-out, opacity 0.2s ease-out;
            overflow: overlay;
            scrollbar-width: thin;
            z-index: 2147483647;
          }

          :host([open]) {
            display: flex;
          }

          :host([open]) .modal {
            transform: scale(1);
            opacity: 1;
          }

          .backdrop {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: transparent;
          }

          ::slotted([slot="close"]) {
            padding: 0;
            position: absolute;
            top: 10px;
            right: 10px;
            background: none;
            border: none;
            cursor: pointer;
          }
        </style>

        <div class="backdrop"></div>
        <div class="modal">
          <slot name="close"></slot>
          <slot></slot>
        </div>
      `;
    }
  }

  customElements.define("pk-modal", PKModal);
};

/************************************************************************************************
PkExpandableContent *****************************************************************************
************************************************************************************************/
if (!customElements.get('pk-expandable-content')) {
  class PkExpandableContent extends HTMLElement {
    constructor() {
      super();
  
      this.content = null;
      this.toggleButton = null;
      this.isExpanded = false;
  
      this.toggleContent = this.toggleContent.bind(this);
    }

    connectedCallback() {
      try {
        this.init();
      } catch (error) {
        console.error('PkExpandableContent initialization error:', error.message);
      }
    }

    disconnectedCallback() {
      this.toggleButton.removeEventListener('click', this.toggleContent);
    }
    
    init() {
      this.content = this.querySelector('[data-content]');
      this.toggleButton = this.querySelector('[data-button]');
  
      if (!this.content || !this.toggleButton) {
        throw new Error('Required data-content or data-button elements are missing in the DOM.');
      }
  
      if (!this.validateMaxHeightVariable()) {
        throw new Error('Missing or invalid --pk-expandable-content-max-height CSS variable. Set a non-negative value.');
      }
  
      if (!this.shouldDisplayToggleButton()) {
        this.toggleButton.remove();
        return;
      }
  
      this.toggleButton.classList.add('pk-expandable-content__toggle-button--visible');
      
      this.initButtonLabels();
      this.setInitialState();
      this.attachEventListeners();
    }
  
    initButtonLabels() {
      const labels = this.toggleButton.getAttribute('data-button');
      if (!labels) {
        throw new Error('data-button attribute is missing or empty.');
      }
  
      const [showMoreLabel, showLessLabel] = labels.split(',');
      if (!showMoreLabel || !showLessLabel) {
        throw new Error('data-button attribute must contain two labels separated by a comma, e.g., "Show more,Show less".');
      }
  
      this.showMoreLabel = showMoreLabel.trim();
      this.showLessLabel = showLessLabel.trim();
    }
  
    setInitialState() {
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
  
      this.content.style.maxHeight = maxHeight;
      this.content.setAttribute('aria-expanded', 'false');
      this.toggleButton.textContent = this.showMoreLabel;
    }
  
    attachEventListeners() {
      this.toggleButton.addEventListener('click', this.toggleContent);
    }
  
    toggleContent() {
      this.isExpanded = !this.isExpanded;
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
  
      this.content.style.maxHeight = this.isExpanded ? 'none' : maxHeight;
      this.content.setAttribute('aria-expanded', this.isExpanded);
      this.toggleButton.textContent = this.isExpanded
        ? this.showLessLabel
        : this.showMoreLabel;
    }
  
    /**
     * Validate the `--pk-expandable-content-max-height` CSS variable to ensure it is valid.
     * @returns {boolean} True if the variable is valid, false otherwise.
     */
    validateMaxHeightVariable() {
      const maxHeight = this.getCSSVariable('--pk-expandable-content-max-height');
      const numericHeight = parseInt(maxHeight, 10);
      
      return !Number.isNaN(numericHeight) && numericHeight >= 0;
    }
  
    /**
     * Determines whether the toggle button should be displayed based on the content's height.
     * @returns {boolean} True if the button should be displayed, false otherwise.
     */
    shouldDisplayToggleButton() {
      const maxHeight = parseInt(this.getCSSVariable('--pk-expandable-content-max-height'), 10);
      const contentHeight = this.content.scrollHeight;
      
      return contentHeight > maxHeight;
    }
  
    /**
     * @param {string} variableName - The name of the CSS variable to retrieve.
     * @returns {string|null} The value of the CSS variable, or null if not set.
     */
    getCSSVariable(variableName) {
      return getComputedStyle(this.content).getPropertyValue(variableName).trim();
    }
  }
  
  customElements.define('pk-expandable-content', PkExpandableContent);
};

/************************************************************************************************
Hide Shopify preview bar for convenience ********************************************************
************************************************************************************************/
window.addEventListener('DOMContentLoaded', () => {
  const shopifyPreviewBar = document.getElementById('PBarNextFrameWrapper');

  if (shopifyPreviewBar) {
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');
    const css = '#PBarNextFrameWrapper { display: none !important; }';
  
    head.appendChild(style); 
    style.appendChild(document.createTextNode(css));
  }
});
